// certificate-validation-page.tsx — PUBLIC page (no auth) reached via
// `?validate` (optionally `?validate=<code>`). The visitor types the code
// printed on the certificate; the backend looks it up and returns the holder's
// name/campaign/date. Wrapped in `.student-portal` + the portal stylesheet so it
// matches the rest of the student portal's look.
import { Card } from '../../../components/card';
import { Button } from '../../../components/button';
import { useEffect, useState, type FormEvent } from 'react';
import { LanguageSelect } from '../../../components/language-select';
import { useTranslation } from 'react-i18next';
import { useValidateCertificate } from '../../../api/student/certificates/hooks';
import '../styles/index.css';
import styles from './certificate-validate.module.css';

function fmtRange(lang: string, start: number | null, end: number | null): string {
  const f = (s: number) =>
    new Intl.DateTimeFormat(lang, { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(s * 1000));
  if (start && end) return `${f(start)} – ${f(end)}`;
  if (end) return f(end);
  if (start) return f(start);
  return '';
}

export function CertificateValidationPage({ initialCode }: { initialCode: string }) {
  const { t, i18n } = useTranslation();
  const validate = useValidateCertificate();
  const [code, setCode] = useState(initialCode);

  // Auto-validate when arriving with a code already in the URL (QR / shared link).
  useEffect(() => {
    if (initialCode.trim()) validate.mutate(initialCode.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (code.trim()) validate.mutate(code.trim());
  };

  const data = validate.data;
  return (
    <div className="portal-shell student-portal">
      <div className={styles.page}>
        <div className={styles.langWrap}>
          <LanguageSelect compact />
        </div>
        <div className={styles.inner}>
          <Card framed padded={false} className={styles.card}>
            <div className={styles.brand}>Ponder</div>
            <h1 className={styles.title}>{t('student.campaign.certificate.validateTitle')}</h1>
            <p className={styles.intro}>{t('student.campaign.certificate.validateIntro')}</p>

            <form className={styles.form} onSubmit={onSubmit}>
              <input
                className={styles.input}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder={t('student.campaign.certificate.codePlaceholder')}
                autoFocus
              />
              <Button type="submit" disabled={validate.isPending || !code.trim()}>
                {validate.isPending ? t('common.loading') : t('student.campaign.certificate.validateBtn')}
              </Button>
            </form>

            {validate.isError && (
              <div className={`${styles.result} ${styles.resultInvalid}`}>
                <div className={styles.icon}>✕</div>
                <p className={styles.headline}>{t('student.campaign.certificate.validateInvalid')}</p>
                <p className={styles.sub}>{t('student.campaign.certificate.validateInvalidHint')}</p>
              </div>
            )}

            {data?.valid && (
              <div className={`${styles.result} ${styles.resultValid}`}>
                <div className={styles.icon}>✓</div>
                <p className={styles.headline}>{t('student.campaign.certificate.validateValid')}</p>
                <p className={styles.label}>{t('student.campaign.certificate.awardedTo')}</p>
                <p className={styles.name}>{data.studentName}</p>
                <p className={styles.campaign}>{data.campaignName}</p>
                <p className={styles.date}>{fmtRange(i18n.language, data.startDateUtc, data.endDateUtc)}</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
